import React, { useEffect, useState, ChangeEvent, useRef } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  FormGroup,
  Button,
  Row,
  Col,
  Label,
  Form,
  Input,
} from 'reactstrap';
import { useAlertPlus, useConfirm } from 'hooks';
import Required from 'components/Required';
import ConfirmModal from 'components/ConfirmModal';
import AlertModal from 'components/AlertModal';
import { useUser } from 'context/UserContext';
import {
  getConsultorioInfoWithStaff,
  putTelefonoConsultorio,
} from '../../features/consultorios/consultoriosAPI';
import { ConsultorioInfoWithStaff } from 'types/Consultorio';

export default function ViewConsultorio() {
  const { id } = useParams();
  const { getMatricula } = useUser();
  const matriculateId = getMatricula();
  if (!matriculateId) {
    return null;
  }

  const [formValidation, setFormValidation] = useState<
    Partial<Record<keyof ConsultorioInfoWithStaff, boolean>>
  >({});

  const [showAlert, message, alertCallback, alert] = useAlertPlus('');
  const [confirm, confirmTitle, confirmAction, setConfirm] = useConfirm(false);

  const formElement = useRef<HTMLFormElement | null>(null);

  const history = useHistory();

  const [isLoading, setLoading] = useState(false);

  const [data, setData] = useState<ConsultorioInfoWithStaff>({
    consultorio_id: 0,
    director: {
      matricula: 0,
      nombre: '',
      apellido: '',
    },
    propietario: {
      cuit: '',
      nombre: '',
    },
    nombre: '',
    calle: '',
    numero: '',
    piso: '',
    departamento: '',
    localidad: '',
    cantidad_gabinetes: 0,
    prefijo_telefono: '',
    numero_telefono: '',
    allStaff: [],
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        getConsultorioInfoWithStaff(id)
          .then(response => {
            if (!!response) {
              setData(response);
            } else {
              throw new Error('No se pudo obtener la información del consultorio');
            }
          })
          .catch(err => {
            alert('No se pudo obtener la información del consultorio');
            console.info(err);
          });
      } catch (err) {
        alert(
          'Se produjo un error al intentar recuperar información inicial. ' +
            'Por favor, intente nuevamente más tarde.',
        );
        console.error('ViewConsultorio', 'Error al cargar datos iniciales');
        console.info(err);
      }
    };

    loadData();
  }, [id]);

  const handleFormValidation = () => {
    let newFormValidation = {};

    let toValidate = [
      ...Array.from(document.getElementsByClassName('form-control')),
    ] as HTMLInputElement[];
    toValidate = toValidate.filter(e => !e.disabled && e.required);

    let missing = false;
    // Validar campos required
    toValidate.forEach(field => {
      if (field.required && !field.disabled && !field.value?.trim()) {
        field.classList.add('is-invalid');

        missing = true;
      } else if (field.inputMode === 'numeric') {
        if (!/^\d+$/.test(field.value)) {
          newFormValidation = {
            ...newFormValidation,
            [field.name]: false,
          };
        }
      } else {
        field.classList.remove('is-invalid');
      }
    });

    if (missing) {
      setFormValidation(newFormValidation);
      alert(
        'Todos los campos marcados con * son obligatorios. ' +
          'Por favor complete los faltantes antes de enviar la solicitud.',
      );
      return false;
    }

    return true;
  };

  // Confirmación de envío de Solicitud
  const handleSendForm = () => {
    if (!handleFormValidation()) {
      return;
    }

    setConfirm(
      true,
      '¿Estás seguro que deseas enviar esta solicitud de consultorio?',
      'send',
    );
  };

  // Gestor de respuestas de confirmaciones
  const handleConfirmation = (action: string) => {
    if (action === 'send') {
      handleSend();
    }

    setConfirm(false);
  };

  // Envío de solicitudes
  const handleSend = async () => {
    setFormValidation({});

    setLoading(true);

    // Preparamos los inputs menos los archivos
    const consultorio: Partial<ConsultorioInfoWithStaff> = {};

    if (!formElement.current) return;

    new FormData(formElement.current).forEach((value, key) => {
      // @ts-ignore
      consultorio[key as keyof ConsultorioInfoWithStaff] = value.toString() as any;
    });

    // Seteamos el id
    consultorio['consultorio_id'] = data.consultorio_id;

    // Enviamos la petición
    await putTelefonoConsultorio(consultorio)
      .then(res => {
        if (!res || res.status != 'ok') {
          // Error interno
          console.error('ViewConsultorio', 'Error al enviar la solicitud');
          console.info(res);
          alert(
            'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
          );
          return;
        }

        alert('Se actualizaron los datos correctamente', () => {
          window.location.reload();
        });
      })
      .catch(err => {
        console.error(
          'ViewConsultorio',
          'Error inesperado al intentar enviar la solicitud',
        );
        console.info(err);
        alert(
          'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const { target } = event;

    const name = target.name;

    let value: string | number | Date | null;
    value = target.value;
    if (
      typeof value === 'string' &&
      ['dni', 'cuit', 'telefono', 'legal_telefono', 'legal_celular'].includes(
        name,
      )
    ) {
      value = !!parseInt(value) ? parseInt(value) : '';
    }

    // Guardamos el nuevo valor
    setData({
      ...data,
      [name]: value,
    });
  };

  return (
    <div className="content">
      <Row>
        <Col sm={12}>
          <Card>
            <CardHeader>
              <Row style={{ alignItems: 'center' }}>
                <Col sm={8}>
                  <CardTitle tag="h5">Consultorio #{ id }</CardTitle>
                </Col>
                <Col sm={4} className="text-right">
                  <Button
                    className="btn-round"
                    color="primary"
                    onClick={() => history.push('/consultorios')}
                  >
                    Volver
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Row>
                <Col sm={12}>
                  <CardTitle>Consultorio</CardTitle>
                </Col>
              </Row>
              <Row>
                <Col sm={6}>
                  <FormGroup>
                    <label className='form-control-label'>Nombre</label>
                    <p>{ data.nombre || '-' }</p>
                  </FormGroup>
                </Col>
                <Col sm={6}>
                  <FormGroup>
                    <label className='form-control-label'>Dirección</label>
                    <p>{ data.calle } { data.numero }, { data.localidad }</p>
                  </FormGroup>
                </Col>
                <Col sm={6}>
                  <FormGroup>
                    <label className='form-control-label'>Director/a Técnico/a</label>
                    <Row>
                      <Col sm={6}>
                        <p>
                          <b>MP { data.director.matricula }</b>: { data.director.apellido } { data.director.nombre }
                        </p>
                      </Col>
                      { data.director.matricula == +matriculateId
                        ?
                          <Col sm={6} className=''>
                            <Link
                              to={`/consultorios/${ id }/update/director/create`}
                              className="btn btn-round btn-primary btn-sm"
                              style={{ padding: '5px 10px' }}
                              title='Modificar Director'
                            >
                              <i className="fa fa-edit"></i>
                            </Link>
                          </Col>
                        : null
                      }
                    </Row>
                  </FormGroup>
                </Col>
                <Col sm={6}></Col>
                <Col sm={6}>
                  <FormGroup>
                    <label className='form-control-label'>Propietario</label>
                    <Row>
                      <Col sm={6}>
                        <p>
                          Nombre/Razon Social: { data.propietario.nombre }
                        </p>
                        <p>
                          CUIT: { data.propietario.cuit }
                        </p>
                      </Col>
                      { data.director.matricula == +matriculateId
                        ?
                          <Col sm={6} className=''>
                            <Link
                              to={`/consultorios/${ id }/update/propietario/create`}
                              className="btn btn-round btn-primary btn-sm"
                              style={{ padding: '5px 10px' }}
                              title='Modificar Director'
                            >
                              <i className="fa fa-edit"></i>
                            </Link>
                          </Col>
                        : null
                      }
                    </Row>
                  </FormGroup>
                </Col>
                <Col sm={6}></Col>
                <Col sm={6}>
                  <FormGroup>
                    <label className='form-control-label'>Información Edilicia</label>
                    <Row>
                      <Col sm={6}>
                        <p>
                          Cantidad de Gabinetes: { data.cantidad_gabinetes }
                        </p>
                      </Col>
                      { data.director.matricula == +matriculateId
                        ?
                          <Col sm={6} className=''>
                            <Link
                              to={`/consultorios/${ id }/update/edilicia/create`}
                              className="btn btn-round btn-primary btn-sm"
                              style={{ padding: '5px 10px' }}
                              title='Modificar Director'
                            >
                              <i className="fa fa-edit"></i>
                            </Link>
                          </Col>
                        : null
                      }
                    </Row>
                  </FormGroup>
                </Col>
                <Col sm={6}></Col>
              </Row>
              { data.director.matricula == +matriculateId
                ?
                  <>
                    <hr className="my-4" />
                    <label className='form-control-label'>Información Adicional</label>
                    <Form innerRef={formElement}>
                      <FormGroup>
                        <Row>
                          <Col sm={1}>
                            <Label for="prefijo_telefono">
                              Prefijo <Required />
                            </Label>
                            <Input
                              id="prefijo_telefono"
                              name="prefijo_telefono"
                              value={data.prefijo_telefono ?? ''}
                              invalid={formValidation.prefijo_telefono === false}
                              onChange={handleInputChange}
                              disabled={isLoading}
                              required
                            />
                          </Col>
                          <Col sm={3}>
                            <Label for="numero_telefono">
                              Teléfono <Required />
                            </Label>
                            <Input
                              id="numero_telefono"
                              name="numero_telefono"
                              value={data.numero_telefono ?? ''}
                              invalid={formValidation.numero_telefono === false}
                              onChange={handleInputChange}
                              disabled={isLoading}
                              required
                            />
                          </Col>
                        </Row>
                      </FormGroup>
                      <FormGroup>
                        <Col sm={3}>
                          <Button
                            type="button"
                            className="btn-round"
                            onClick={handleSendForm}
                            disabled={isLoading}
                          >
                            Cambiar
                          </Button>
                        </Col>
                      </FormGroup>
                    </Form>
                  </>
                :
                  <Row>
                    <Col sm={6}>
                      <FormGroup>
                        <label className='form-control-label'>Información Adicional</label>
                        <Row>
                          <Col sm={6}>
                            <p>
                              Teléfono: ({ data.prefijo_telefono }) { data.numero_telefono }
                            </p>
                          </Col>
                        </Row>
                      </FormGroup>
                    </Col>
                    <Col sm={6}></Col>
                  </Row>
              }
              <hr className="my-4" />
              <Col sm={8}>
                <Row>
                  <Col sm={8}>
                    <CardTitle>Staff</CardTitle>
                  </Col>
                </Row>
              </Col>
              <Col sm={8}>
                {data.allStaff.map((staff, index) => (
                  <Row key={index} className='align-items-center'>
                    <Col sm={8}>
                      <FormGroup>
                        <label className='form-control-label'>
                          { staff.is_codirector ? 'Codirector/a' : 'Trabajador/a' }&nbsp;
                        </label>
                        <b>MP { staff.matricula }</b>:&nbsp;
                        { staff.apellido } { staff.nombre }
                      </FormGroup>
                    </Col>
                  </Row>
                ))}
              </Col>
              <AlertModal
                isOpen={showAlert}
                message={message}
                onClose={alertCallback}
              />
              <ConfirmModal
                isOpen={confirm}
                title={confirmTitle}
                action={confirmAction}
                onClose={() => setConfirm(false)}
                onConfirm={handleConfirmation}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
